import type {
  Purchase, PurchaseDetail, Receiving, ReceivingDetail,
} from './types';

export interface PurchasesQuery {
  from: string;
  to: string;
  sid?: number | null;
  sup_id?: string;
  status?: 'all' | 'open' | 'partial' | 'closed';
  limit?: number;
}

export interface ReceivingsQuery {
  from: string;
  to: string;
  sid?: number | null;
  sup_id?: string;
  completeness?: 'all' | 'short' | 'full' | 'over';
  limit?: number;
}

function buildQS(params: { [k: string]: unknown }): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? '?' + parts.join('&') : '';
}

async function fetchJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return (await res.json()) as T;
}

/**
 * The cookie is `pacebi_csrf` (internal/auth/csrf.go) — same name every other
 * api/*.ts write helper reads. Getting it wrong sends an empty X-CSRF-Token
 * and RequireCSRF 403s every write.
 */
function csrf(): string {
  const m = document.cookie.match(/(?:^|;\s*)pacebi_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

/**
 * Emails a purchase order (JCD-23): posts the browser-built CSV (required)
 * and PDF (optional) as multipart, same as uploadPayable — no Content-Type
 * header, the browser sets it so the boundary is included.
 */
export async function sendPurchaseEmail(
  sid: number,
  onum: number,
  opts: { to: string; csv: Blob; pdf?: Blob },
): Promise<void> {
  const fd = new FormData();
  fd.append('to', opts.to);
  fd.append('csv', opts.csv, 'orden.csv');
  if (opts.pdf) fd.append('pdf', opts.pdf, 'orden.pdf');
  const res = await fetch(`/api/purchases/${sid}/${onum}/email`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrf() },
    body: fd,
  });
  if (res.status === 401) throw new Error('unauthenticated');
  if (res.status === 403) throw new Error('forbidden');
  if (!res.ok) throw new Error((await res.text().catch(() => '')) || `HTTP ${res.status}`);
}

export function fetchPurchases(q: PurchasesQuery): Promise<Purchase[]> {
  return fetchJSON<Purchase[]>('/api/purchases' + buildQS({ ...q }));
}

export function fetchPurchaseDetail(sid: number, onum: number): Promise<PurchaseDetail> {
  return fetchJSON<PurchaseDetail>(`/api/purchases/${sid}/${onum}`);
}

export function fetchReceivings(q: ReceivingsQuery): Promise<Receiving[]> {
  return fetchJSON<Receiving[]>('/api/receivings' + buildQS({ ...q }));
}

export function fetchReceivingDetail(sid: number, onum: number, receive_no: number): Promise<ReceivingDetail> {
  return fetchJSON<ReceivingDetail>(`/api/receivings/${sid}/${onum}/${receive_no}`);
}
